import { useCallback, useEffect, useState } from 'react'
import api from '../utils/api'
import { TimeWindow, TimeWindowFilter } from '@/components/TimeWindowFilter'
import { Button } from '@/components/Button'
import { useToast } from '@/components/Toast'
import { apiErrorMessage } from '@/utils/apiErrors'

interface Task {
  id: string
  title: string
  task_type: string
  status: string
  risk_level: string
  schedule_kind?: string
  next_run_at?: string
  created_at?: string
}

export default function BeaconTasks() {
  const { toast } = useToast()
  const [window, setWindow] = useState<TimeWindow>('week')
  const [rows, setRows] = useState<Task[]>([])
  const [title, setTitle] = useState('')
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/v1/beacon/tasks', { params: { window } })
      setRows(res.data.tasks || [])
    } catch (err: any) {
      toast.error('Failed to load tasks', apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [window, toast])

  useEffect(() => { load() }, [load])

  const create = async () => {
    if (!title.trim()) return
    try {
      await api.post('/api/v1/beacon/tasks', {
        title,
        task_type: 'manual',
        schedule_kind: 'interval',
        schedule_expr: '3600',
        description: 'Created from Beacon Tasks UI',
      })
      setTitle('')
      toast.success('Task scheduled')
      await load()
    } catch (err: any) {
      toast.error('Create failed', apiErrorMessage(err))
    }
  }

  const runDue = async () => {
    try {
      const res = await api.post('/api/v1/beacon/tasks/run-due')
      toast.success('Scheduler', `Ran ${res.data.ran} due task(s)`)
      await load()
    } catch (err: any) {
      toast.error('Run due failed', apiErrorMessage(err))
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-slate-900">Tasks</h2>
          <p className="text-sm text-slate-600">Create, schedule, and review Beacon work.</p>
        </div>
        <div className="flex items-center gap-2">
          <TimeWindowFilter value={window} onChange={setWindow} />
          <Button variant="secondary" onClick={runDue}>Run due</Button>
        </div>
      </div>

      <div className="flex gap-2 rounded-xl border border-slate-200 bg-white p-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="New hourly task title…"
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
        />
        <Button onClick={create}>Schedule hourly</Button>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Risk</th>
              <th className="px-4 py-3">Next run</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No tasks yet.</td></tr>}
            {rows.map((t) => (
              <tr key={t.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium text-slate-800">{t.title}</td>
                <td className="px-4 py-3 text-slate-600">{t.task_type}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{t.status}</span></td>
                <td className="px-4 py-3 text-slate-600">{t.risk_level}</td>
                <td className="px-4 py-3 text-slate-500">{t.next_run_at ? new Date(t.next_run_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
